import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

const paymentTermsOptions = [
  { value: 'PREPAID', label: 'Prepaid' },
  { value: 'CREDIT_15', label: 'Net 15 Days' },
  { value: 'CREDIT_30', label: 'Net 30 Days' },
  { value: 'CREDIT_60', label: 'Net 60 Days' },
  { value: 'CREDIT_90', label: 'Net 90 Days' },
] as const;

const customerTypeOptions = [
  { value: 'BUSINESS', label: 'Business' },
  { value: 'DISTRIBUTOR', label: 'Distributor' },
  { value: 'RETAILER', label: 'Retailer' },
  { value: 'GOVERNMENT', label: 'Government' },
  { value: 'NONPROFIT', label: 'Non-Profit' },
] as const;

type PaymentTerms = (typeof paymentTermsOptions)[number]['value'];
type CustomerType = (typeof customerTypeOptions)[number]['value'];

interface CustomerFormData {
  // Company
  companyName: string;
  tradeName?: string;
  // Primary Contact
  contactName: string;
  contactTitle?: string;
  email: string;
  phone?: string;
  secondaryPhone?: string;
  // Address
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  // Billing Address
  billingAddressLine1?: string;
  billingAddressLine2?: string;
  billingCity?: string;
  billingState?: string;
  billingPostalCode?: string;
  billingCountry?: string;
  // Tax & Legal
  taxId?: string;
  taxExempt: boolean;
  taxExemptReason?: string;
  // Credit & Payment
  paymentTerms: PaymentTerms;
  creditLimitEnabled: boolean;
  creditLimit: number;
  creditCurrency: string;
  // Classification
  customerType: CustomerType;
  industry?: string;
  // Internal
  notes?: string;
  internalNotes?: string;
  isActive: boolean;
}

interface TradingCustomer {
  id: string;
  companyName: string;
  tradeName?: string | null;
  contactName: string;
  contactTitle?: string | null;
  email: string;
  phone?: string | null;
  secondaryPhone?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  billingAddressLine1?: string | null;
  billingAddressLine2?: string | null;
  billingCity?: string | null;
  billingState?: string | null;
  billingPostalCode?: string | null;
  billingCountry?: string | null;
  taxId?: string | null;
  taxExempt: boolean;
  taxExemptReason?: string | null;
  paymentTerms: string;
  creditLimitEnabled: boolean;
  creditLimit: number | { toNumber: () => number };
  creditCurrency: string;
  customerType: CustomerType;
  industry?: string | null;
  notes?: string | null;
  internalNotes?: string | null;
  isActive: boolean;
}

interface TradingCustomerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer?: TradingCustomer;
}

export function TradingCustomerModal({
  open,
  onOpenChange,
  customer,
}: TradingCustomerModalProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const isEditing = !!customer;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<CustomerFormData>({
    defaultValues: {
      companyName: '',
      tradeName: '',
      contactName: '',
      contactTitle: '',
      email: '',
      phone: '',
      secondaryPhone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      billingAddressLine1: '',
      billingAddressLine2: '',
      billingCity: '',
      billingState: '',
      billingPostalCode: '',
      billingCountry: '',
      taxId: '',
      taxExempt: false,
      taxExemptReason: '',
      paymentTerms: 'CREDIT_30',
      creditLimitEnabled: false,
      creditLimit: 0,
      creditCurrency: 'EUR',
      customerType: 'BUSINESS',
      industry: '',
      notes: '',
      internalNotes: '',
      isActive: true,
    },
  });

  const utils = trpc.useUtils();

  const createMutation = trpc.tradingCustomer.create.useMutation({
    onSuccess: () => {
      utils.tradingCustomer.list.invalidate();
      utils.tradingCustomer.listForSelect.invalidate();
      showSuccessToast(t('customers.customerCreated'));
      onOpenChange(false);
      reset();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const updateMutation = trpc.tradingCustomer.update.useMutation({
    onSuccess: () => {
      utils.tradingCustomer.list.invalidate();
      utils.tradingCustomer.listForSelect.invalidate();
      if (customer?.id) {
        utils.tradingCustomer.byId.invalidate(customer.id);
      }
      showSuccessToast(t('customers.customerUpdated'));
      onOpenChange(false);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  useEffect(() => {
    if (open) {
      if (customer) {
        // Prisma Decimal may come over the wire as a Decimal object (when
        // superjson is in play) or as a plain string (raw JSON), depending
        // on the transport. Both must be coerced to a number — passing the
        // string back through to the API on save trips the Zod number check.
        const creditLimitRaw = customer.creditLimit;
        const creditLimitValue =
          creditLimitRaw == null
            ? 0
            : typeof creditLimitRaw === 'object'
            ? creditLimitRaw.toNumber()
            : Number(creditLimitRaw);
        reset({
          companyName: customer.companyName,
          tradeName: customer.tradeName || '',
          contactName: customer.contactName,
          contactTitle: customer.contactTitle || '',
          email: customer.email,
          phone: customer.phone || '',
          secondaryPhone: customer.secondaryPhone || '',
          addressLine1: customer.addressLine1 || '',
          addressLine2: customer.addressLine2 || '',
          city: customer.city || '',
          state: customer.state || '',
          postalCode: customer.postalCode || '',
          country: customer.country || '',
          billingAddressLine1: customer.billingAddressLine1 || '',
          billingAddressLine2: customer.billingAddressLine2 || '',
          billingCity: customer.billingCity || '',
          billingState: customer.billingState || '',
          billingPostalCode: customer.billingPostalCode || '',
          billingCountry: customer.billingCountry || '',
          taxId: customer.taxId || '',
          taxExempt: customer.taxExempt,
          taxExemptReason: customer.taxExemptReason || '',
          paymentTerms: customer.paymentTerms as PaymentTerms,
          creditLimitEnabled: customer.creditLimitEnabled,
          creditLimit: creditLimitValue,
          creditCurrency: customer.creditCurrency,
          customerType: customer.customerType,
          industry: customer.industry || '',
          notes: customer.notes || '',
          internalNotes: customer.internalNotes || '',
          isActive: customer.isActive,
        });
      } else {
        reset({
          companyName: '',
          tradeName: '',
          contactName: '',
          contactTitle: '',
          email: '',
          phone: '',
          secondaryPhone: '',
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
          billingAddressLine1: '',
          billingAddressLine2: '',
          billingCity: '',
          billingState: '',
          billingPostalCode: '',
          billingCountry: '',
          taxId: '',
          taxExempt: false,
          taxExemptReason: '',
          paymentTerms: 'CREDIT_30',
          creditLimitEnabled: false,
          creditLimit: 0,
          creditCurrency: 'EUR',
          customerType: 'BUSINESS',
          industry: '',
          notes: '',
          internalNotes: '',
          isActive: true,
        });
      }
    }
  }, [open, customer, reset]);

  const onSubmit = (data: CustomerFormData) => {
    const payload = {
      companyName: data.companyName,
      tradeName: data.tradeName || null,
      contactName: data.contactName,
      contactTitle: data.contactTitle || null,
      email: data.email,
      phone: data.phone || null,
      secondaryPhone: data.secondaryPhone || null,
      addressLine1: data.addressLine1 || null,
      addressLine2: data.addressLine2 || null,
      city: data.city || null,
      state: data.state || null,
      postalCode: data.postalCode || null,
      country: data.country || null,
      billingAddressLine1: data.billingAddressLine1 || null,
      billingAddressLine2: data.billingAddressLine2 || null,
      billingCity: data.billingCity || null,
      billingState: data.billingState || null,
      billingPostalCode: data.billingPostalCode || null,
      billingCountry: data.billingCountry || null,
      taxId: data.taxId || null,
      taxExempt: data.taxExempt,
      taxExemptReason: data.taxExemptReason || null,
      paymentTerms: data.paymentTerms,
      creditLimitEnabled: data.creditLimitEnabled,
      // Defensive coerce — react-hook-form's valueAsNumber only fires when
      // the input is rendered. If the user toggles the limit off and never
      // touches the field, the form value can stay as the original string.
      creditLimit: typeof data.creditLimit === 'string' ? Number(data.creditLimit) || 0 : data.creditLimit ?? 0,
      creditCurrency: data.creditCurrency,
      customerType: data.customerType,
      industry: data.industry || null,
      notes: data.notes || null,
      internalNotes: data.internalNotes || null,
      isActive: data.isActive,
    };

    if (isEditing && customer) {
      updateMutation.mutate({ id: customer.id, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const creditLimitEnabled = watch('creditLimitEnabled');
  const taxExempt = watch('taxExempt');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? t('customers.editCustomer')
              : t('customers.addCustomer')}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t('customers.editCustomerDescription')
              : t('customers.addCustomerDescription')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Tabs defaultValue="company" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="company">
                {t('customers.tabs.company')}
              </TabsTrigger>
              <TabsTrigger value="address">
                {t('customers.tabs.address')}
              </TabsTrigger>
              <TabsTrigger value="financial">
                {t('customers.tabs.financial')}
              </TabsTrigger>
              <TabsTrigger value="notes">
                {t('customers.tabs.notes')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="company" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="companyName">
                    {t('customers.companyName')} *
                  </Label>
                  <Input
                    id="companyName"
                    {...register('companyName', {
                      required: t('customers.companyNameRequired'),
                    })}
                  />
                  {errors.companyName && (
                    <p className="text-sm text-red-500">
                      {errors.companyName.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tradeName">{t('customers.tradeName')}</Label>
                  <Input id="tradeName" {...register('tradeName')} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="customerType">
                    {t('customers.customerType')}
                  </Label>
                  <Combobox
                    options={customerTypeOptions.map((o) => ({
                      value: o.value,
                      label: o.label,
                    }))}
                    value={watch('customerType')}
                    onValueChange={(value) =>
                      setValue('customerType', value as CustomerType)
                    }
                    placeholder={t('customers.selectCustomerType')}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="industry">{t('customers.industry')}</Label>
                  <Input id="industry" {...register('industry')} />
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">
                  {t('customers.primaryContact')}
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contactName">
                      {t('customers.contactName')} *
                    </Label>
                    <Input
                      id="contactName"
                      {...register('contactName', {
                        required: t('customers.contactNameRequired'),
                      })}
                    />
                    {errors.contactName && (
                      <p className="text-sm text-red-500">
                        {errors.contactName.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contactTitle">
                      {t('customers.contactTitle')}
                    </Label>
                    <Input id="contactTitle" {...register('contactTitle')} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('customers.email')} *</Label>
                    <Input
                      id="email"
                      type="email"
                      {...register('email', {
                        required: t('customers.emailRequired'),
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: t('customers.invalidEmail'),
                        },
                      })}
                    />
                    {errors.email && (
                      <p className="text-sm text-red-500">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">{t('customers.phone')}</Label>
                    <Input id="phone" {...register('phone')} />
                  </div>
                </div>

                <div className="space-y-2 mt-4">
                  <Label htmlFor="secondaryPhone">
                    {t('customers.secondaryPhone')}
                  </Label>
                  <Input id="secondaryPhone" {...register('secondaryPhone')} />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  {...register('isActive')}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="isActive">{t('customers.isActive')}</Label>
              </div>
            </TabsContent>

            <TabsContent value="address" className="space-y-4">
              <div>
                <h4 className="font-medium mb-3">
                  {t('customers.primaryAddress')}
                </h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="addressLine1">
                      {t('customers.addressLine1')}
                    </Label>
                    <Input id="addressLine1" {...register('addressLine1')} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="addressLine2">
                      {t('customers.addressLine2')}
                    </Label>
                    <Input id="addressLine2" {...register('addressLine2')} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">{t('customers.city')}</Label>
                      <Input id="city" {...register('city')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">{t('customers.state')}</Label>
                      <Input id="state" {...register('state')} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="postalCode">
                        {t('customers.postalCode')}
                      </Label>
                      <Input id="postalCode" {...register('postalCode')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">{t('customers.country')}</Label>
                      <Input id="country" {...register('country')} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">
                  {t('customers.billingAddress')}
                </h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="billingAddressLine1">
                      {t('customers.addressLine1')}
                    </Label>
                    <Input
                      id="billingAddressLine1"
                      {...register('billingAddressLine1')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="billingAddressLine2">
                      {t('customers.addressLine2')}
                    </Label>
                    <Input
                      id="billingAddressLine2"
                      {...register('billingAddressLine2')}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="billingCity">{t('customers.city')}</Label>
                      <Input id="billingCity" {...register('billingCity')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="billingState">
                        {t('customers.state')}
                      </Label>
                      <Input id="billingState" {...register('billingState')} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="billingPostalCode">
                        {t('customers.postalCode')}
                      </Label>
                      <Input
                        id="billingPostalCode"
                        {...register('billingPostalCode')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="billingCountry">
                        {t('customers.country')}
                      </Label>
                      <Input
                        id="billingCountry"
                        {...register('billingCountry')}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="financial" className="space-y-4">
              <div>
                <h4 className="font-medium mb-3">{t('customers.taxInfo')}</h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="taxId">{t('customers.taxId')}</Label>
                    <Input id="taxId" {...register('taxId')} />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="taxExempt"
                      {...register('taxExempt')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="taxExempt">{t('customers.taxExempt')}</Label>
                  </div>

                  {taxExempt && (
                    <div className="space-y-2">
                      <Label htmlFor="taxExemptReason">
                        {t('customers.taxExemptReason')}
                      </Label>
                      <Input
                        id="taxExemptReason"
                        {...register('taxExemptReason')}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">
                  {t('customers.paymentTermsSection')}
                </h4>
                <div className="space-y-2">
                  <Label>{t('customers.paymentTerms')}</Label>
                  <Combobox
                    options={paymentTermsOptions.map((o) => ({
                      value: o.value,
                      label: o.label,
                    }))}
                    value={watch('paymentTerms')}
                    onValueChange={(value) =>
                      setValue('paymentTerms', value as PaymentTerms)
                    }
                    placeholder={t('customers.selectPaymentTerms')}
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">
                  {t('customers.creditLimit')}
                </h4>
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="creditLimitEnabled"
                      {...register('creditLimitEnabled')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="creditLimitEnabled">
                      {t('customers.enableCreditLimit')}
                    </Label>
                  </div>

                  {creditLimitEnabled && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="creditLimit">
                          {t('customers.creditLimitAmount')}
                        </Label>
                        <Input
                          id="creditLimit"
                          type="number"
                          step="0.01"
                          {...register('creditLimit', { valueAsNumber: true })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="creditCurrency">
                          {t('customers.currency')}
                        </Label>
                        <Input
                          id="creditCurrency"
                          {...register('creditCurrency')}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="notes" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="notes">{t('customers.notes')}</Label>
                <Textarea id="notes" {...register('notes')} rows={4} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="internalNotes">
                  {t('customers.internalNotes')}
                </Label>
                <Textarea
                  id="internalNotes"
                  {...register('internalNotes')}
                  rows={4}
                />
                <p className="text-sm text-muted-foreground">
                  {t('customers.internalNotesHint')}
                </p>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {tCommon('cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? tCommon('save') : tCommon('create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
